/* eslint-disable spire/export-styles */
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { SafeDictionary } from "@insite/client-framework/Common/Types";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";

import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getCategoriesDataView } from "@insite/client-framework/Store/Data/Categories/CategoriesSelectors";
import { canAddToCart, hasEnoughInventory } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import addToCart from "@insite/client-framework/Store/Pages/Cart/Handlers/AddToCart";
import translate from "@insite/client-framework/Translate";
import { CartLineModel, ProductSubscriptionDto } from "@insite/client-framework/Types/ApiModels";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography from "@insite/mobius/Typography";
import { addToCartEvent, selectPromotion } from "@WhiteCap/Common/Utillities/CustomTracking";
import guestRFQCartHandler from "@WhiteCap/Handlers/GuestRFQ/GuestRFQCartHandler";
import useRFQCartProduct from "@WhiteCap/Hooks/useRequestForQuote";
import { SpecialCategoryContext } from "@WhiteCap/Widgets/GuestRFQ/GuestProductListCardList";
import React, { useContext, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

// EC-234 : SubTask - EC-513 = Implement functionality on CTA "Add to Quote" and QTY Box

interface OwnProps {
    labelOverride?: string;
    extendedStyles?: ButtonPresentationProps;
    notes?: string;
    configurationSelection?: SafeDictionary<string>;
    configurationCompleted?: boolean;
    variantSelectionCompleted?: boolean;
    subscription?: ProductSubscriptionDto;
    onAddToCartClick?: () => void;
}

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasProductContext;

const mapStateToProps = (state: ApplicationState, props: OwnProps & HasProductContext) => {
    const {
        productSettings: { showAddToCartConfirmationDialog },
        cartSettings: { addToCartPopupTimeout },
    } = getSettingsCollection(state);

    const isCartLoading = getCurrentCartState(state).isLoading;
    const { isUpdatingCart } = state.context;
    const productDetails = props.productContext;
    const categoriesId = state.pages.productList.filteredApiParameter?.categoryId;

    let category = null;

    if (categoriesId) {
        category = state.data.categories.byId[categoriesId];
    }

    return {
        canAddToCart: canAddToCart(
            state,
            props.productContext.product,
            props.productContext.productInfo,
            props.configurationCompleted,
            props.variantSelectionCompleted,
            props.configurationSelection,
        ),
        hasEnoughInventory: hasEnoughInventory(state, props.productContext),
        isAddToCartButtonDisabled: isCartLoading || isUpdatingCart,
        showAddToCartConfirmationDialog,
        addToCartPopupTimeout,
        productDetails,
        session: getSession(state),
        category,
    };
};

const mapDispatchToProps = {
    addToCart: makeHandlerChainAwaitable<Parameters<typeof addToCart>[0], CartLineModel>(addToCart),
    guestRFQCartHandler,
};

export const GuestProductAddToQuoteButtonStyles: ButtonPresentationProps = {
    css: css`
        width: 70% !important;
    `,
};

export const ProductAddedToQuoteMessage = () => {
    return (
        <StyledWrapper>
            <Typography>{siteMessage("Cart_ProductAddedToQuote")}</Typography>
        </StyledWrapper>
    );
};

const GuestProductAddToQuoteButton = ({
    productContext: {
        product,
        productInfo: { qtyOrdered },
    },
    hasEnoughInventory,
    isAddToCartButtonDisabled,
    showAddToCartConfirmationDialog,
    addToCartPopupTimeout,
    addToCart,
    canAddToCart,
    labelOverride,
    extendedStyles,
    notes,
    configurationSelection,
    configurationCompleted,
    variantSelectionCompleted,
    subscription,
    onAddToCartClick,
    guestRFQCartHandler,
    productDetails,
    session,
    category,
    ...otherProps
}: Props) => {
    const toasterContext = useContext(ToasterContext);
    const [styles] = useState(() => mergeToNew(GuestProductAddToQuoteButtonStyles, extendedStyles));
    const { cart, setCartToLocalStorage } = useRFQCartProduct();
    const context = useContext(SpecialCategoryContext);

    React.useEffect(() => {
        if (cart) {
            guestRFQCartHandler({ quoteCount: cart.quoteCount });
        }
    }, [cart]);

    const addToQuoteHandler = () => {
        onAddToCartClick?.();
        setCartToLocalStorage(product, qtyOrdered);

        if (showAddToCartConfirmationDialog) {
            toasterContext.addToast({
                body: <ProductAddedToQuoteMessage />,
                messageType: "success",
                timeoutLength: addToCartPopupTimeout,
            });
        }
        if (context?.specialCategory && category?.shortDescription) {   
            const categoryNames = context.specialCategory.split(",");
            const categoryName = category?.shortDescription;
            if (
                categoryName &&
                categoryNames.map(item => item.trim().toLowerCase()).includes(categoryName.toLowerCase())
            ) {
                if (productDetails.product.badges && productDetails.product.badges.length > 0) {
                    selectPromotion({
                        event: "select_promotion",
                        promotion_id: productDetails.product.badges[0].id,
                        promotion_name: productDetails.product.badges[0].name,
                        items: [
                            {
                                item_id: productDetails.product.productNumber,
                                item_name: productDetails.product.productTitle,
                                price: 0,
                                qtyOrdered,
                                item_brand: productDetails.product.brand?.name || "",
                            },
                        ],
                    });
                } else {
                    selectPromotion({
                        event: "select_promotion",
                        promotion_id: category.id,
                        promotion_name: category.name,
                        items: [
                            {
                                item_id: productDetails.product.productNumber,
                                item_name: productDetails.product.productTitle,
                                price: 0,
                                qtyOrdered,
                                item_brand: productDetails.product.brand?.name || "",
                            },
                        ],
                    });
                }
            }
        } else if (productDetails.product.badges && productDetails.product.badges.length > 0) {
            selectPromotion({
                event: "select_promotion",
                promotion_id: productDetails.product.badges[0].id,
                promotion_name: productDetails.product.badges[0].name,
                items: [
                    {
                        item_id: productDetails.product.productNumber,
                        item_name: productDetails.product.productTitle,
                        price: 0,
                        qtyOrdered,
                        item_brand: productDetails.product.brand?.name || "",
                    },
                ],
            });
        }

        // EC-1485 - GTM implementation for guest user add_to_cart event
        addToCartEvent({
            event: "add_to_cart",
            ecommerce: {
                currency: session?.currency?.currencyCode,
                value: 0,
                items: [
                    {
                        item_name: product?.productTitle,
                        item_id: product?.productNumber,
                        quantity: qtyOrdered ?? 1,
                        price: 0,
                        item_brand: product?.brand?.name ? product?.brand?.name : "",
                    },
                ],
            },
        });
    };

    return (
        <Button
            {...styles}
            onClick={addToQuoteHandler}
            disabled={qtyOrdered <= 0 || isAddToCartButtonDisabled}
            {...otherProps}
        >
            {labelOverride ?? translate("ADD TO QUOTE")}
        </Button>
    );
};
export default withProductContext(connect(mapStateToProps, mapDispatchToProps)(GuestProductAddToQuoteButton));

// EC-234 : SubTask - EC-513 = Implement functionality on CTA "Add to Quote" and QTY Box
